import React from 'react';

interface Props {
  /** Opens the multi-value custom-search box. */
  onCustomSearch?: () => void;
  /** Highlight the Custom Search button when a custom search is applied. */
  customSearchActive?: boolean;
  /** Opens the condition-builder custom filter (TemplateCustomFilterDialog). */
  onCustomFilter?: () => void;
  /** Highlight the Custom Filter button when a custom filter is applied. */
  customFilterActive?: boolean;
  /** Opens the New Template dialog (Move Type + Bill To + Customer). */
  onCreateTemplate?: () => void;
  /** Whether the user may create templates — hides New Template when false. */
  canCreate?: boolean;
}

/** Templates landing header: New Template (permission gated — hidden entirely for
 *  read-only users) + Custom Filter + Custom Search. Borrows QuotesPageHeader's
 *  button family; the primary sits left of the two narrowing controls by design.
 *  Custom Filter and Custom Search are independent — the backend ANDs them, so
 *  both can be active (and both banners shown) at once. */
const TemplatesPageHeader: React.FC<Props> = ({
  onCustomSearch, customSearchActive, onCustomFilter, customFilterActive, onCreateTemplate, canCreate,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-nowrap px-[22px] pt-3.5 pb-2.5 border-b border-slate-200 dark:border-slate-700">
        <h1 className="m-0 text-[1.0625rem] font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2.5 tracking-[-0.01em] whitespace-nowrap min-w-0 flex-shrink">
          {/* The match count lives in the TanTable header below — no badge here. */}
          <span className="text-blue-600 font-semibold">Templates</span>
        </h1>
        <div className="flex items-center gap-4">
          {canCreate && onCreateTemplate && (
            <button
              type="button"
              onClick={onCreateTemplate}
              title="New Template"
              className="px-3 py-[7px] text-[0.78125rem] font-medium border rounded-md cursor-pointer inline-flex items-center gap-1.5 bg-blue-600 text-white border-blue-700 hover:bg-blue-700"
            >
              New Template
            </button>
          )}
          <button
            type="button"
            onClick={onCustomFilter}
            aria-pressed={!!customFilterActive}
            title={customFilterActive ? 'Custom filter applied' : 'Custom Filter'}
            className={`px-3 py-[7px] text-[0.78125rem] font-medium border rounded-md cursor-pointer inline-flex items-center gap-1.5 ${
              customFilterActive
                ? 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700 ring-2 ring-indigo-300 dark:border-indigo-500 dark:ring-indigo-700'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600'
            }`}
          >
            <span className={`text-[0.625rem] ${customFilterActive ? 'text-indigo-200' : 'text-slate-400 dark:text-slate-500'}`}>[f]</span>
            Custom Filter
            {customFilterActive && <span className="w-1.5 h-1.5 rounded-full bg-white/90" aria-hidden="true" />}
          </button>
          <button
            type="button"
            onClick={onCustomSearch}
            aria-pressed={!!customSearchActive}
            title={customSearchActive ? 'Custom search applied' : 'Custom Search'}
            className={`px-3 py-[7px] text-[0.78125rem] font-medium border rounded-md cursor-pointer inline-flex items-center gap-1.5 ${
              customSearchActive
                ? 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700 ring-2 ring-indigo-300 dark:border-indigo-500 dark:ring-indigo-700'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600'
            }`}
          >
            <span className={`text-[0.625rem] ${customSearchActive ? 'text-indigo-200' : 'text-slate-400 dark:text-slate-500'}`}>[m]</span>
            Custom Search
            {customSearchActive && <span className="w-1.5 h-1.5 rounded-full bg-white/90" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatesPageHeader;
